import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';
import App from './App';
import { SnackbarProvider } from './contexts/SnackbarContext';
import { PublicConfigProvider } from './contexts/PublicConfigContext';
import { loadPublicConfig } from './lib/publicConfig';
import SystemStatusGate from './components/SystemStatusGate';

// Start fetching public config immediately — fire and forget.
// App renders with env fallbacks; real values populate on resolve.
loadPublicConfig();

// Unregister any old service workers to prevent caching issues
// This ensures users always get the latest version
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(registration => {
      registration.unregister();
      console.log('🧹 Unregistered old service worker');
    });
  });
}

const attemptChunkRecoveryReload = () => {
  const reloadKey = 'chunk_reload_attempted';
  const lastReload = sessionStorage.getItem(reloadKey);
  const now = Date.now();

  if (!lastReload || (now - parseInt(lastReload)) > 10000) {
    sessionStorage.setItem(reloadKey, now.toString());

    const url = new URL(window.location.href);
    url.searchParams.set('v', now.toString());
    window.location.replace(url.toString());
    return true;
  }

  console.error('Chunk loading failed after reload attempt. Please clear your browser cache.');
  return false;
};

// Handle chunk loading errors (happens when app is updated and user has stale cache)
// This will automatically reload the page when a dynamic import fails
window.addEventListener('error', (event) => {
  const message = event?.message || event?.error?.message || '';
  // Check if it's a chunk loading error
  if (message && (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk') ||
    message.includes('Importing a module script failed')
  )) {
    console.warn('🔄 Chunk loading failed, reloading page to get latest version...');
    attemptChunkRecoveryReload();
  }
});

// Also handle unhandled promise rejections for dynamic imports
window.addEventListener('unhandledrejection', (event) => {
  const reason = event?.reason;
  const reasonMessage = (typeof reason === 'string')
    ? reason
    : (reason?.message || '');
  const reasonName = (typeof reason === 'object' && reason) ? reason.name : '';

  if (reasonMessage && (
    reasonMessage.includes('Failed to fetch dynamically imported module') ||
    reasonMessage.includes('Loading chunk') ||
    reasonMessage.includes('Importing a module script failed') ||
    (reasonName === 'TypeError' && reasonMessage.includes('Failed to fetch'))
  )) {
    console.warn('🔄 Dynamic import failed, reloading page...');
    attemptChunkRecoveryReload();
  }
});

window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  console.warn('🔄 Vite preload failed, reloading page...');
  attemptChunkRecoveryReload();
});

const root = createRoot(document.getElementById('root'));
root.render(
  <HelmetProvider>
    <PublicConfigProvider>
      <SystemStatusGate>
        <SnackbarProvider>
          <App />
        </SnackbarProvider>
      </SystemStatusGate>
    </PublicConfigProvider>
  </HelmetProvider>
);
